import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Alert from "reactstrap/lib/Alert";
import Col from "reactstrap/lib/Col";
import Container from "reactstrap/lib/Container";
import Nav from "reactstrap/lib/Nav";
import NavItem from "reactstrap/lib/NavItem";
import NavLink from "reactstrap/lib/NavLink";
import Row from "reactstrap/lib/Row";
import Button from "reactstrap/lib/Button";
import Spinner from "reactstrap/lib/Spinner";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { GetDailyAnalysis } from "../../actions/adminQueries";
import {
  dateAddDays,
  dateIsSameDay,
  formatDate,
} from "../../Utilities/dateUtils";
import {
  AnalysisTabs,
  FindingTabs,
  findingsForTab,
} from "./DailyAnalysisParts";
import DailyAnalysisOverview from "./DailyAnalysisOverview";
import DailyAnalysisBackend from "./DailyAnalysisBackend";
import DailyAnalysisClient from "./DailyAnalysisClient";
import DailyAnalysisSupport from "./DailyAnalysisSupport";
import DailyAnalysisApl from "./DailyAnalysisApl";

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

function DailyAnalysis() {
  const intl = useIntl();
  const [date, setDate] = useState(yesterday);
  const [activeTab, setActiveTab] = useState(AnalysisTabs.OVERVIEW);
  const dateStamp = formatDate(date, "yyyy-MM-dd");
  const latest = yesterday();
  const atLatest = dateIsSameDay(date, latest);
  const { data, isLoading, isError, error } = GetDailyAnalysis(dateStamp);

  const notFound = isError && error?.response?.status === 404;

  const tabs = [
    { id: AnalysisTabs.OVERVIEW, labelId: "DailyAnalysisOverview", badge: 0 },
    ...FindingTabs.map((tab) => ({
      ...tab,
      badge: findingsForTab(data, tab.id).length,
    })),
    // No findings belong to the app tab, so it stays out of FindingTabs and the overview's table.
    { id: AnalysisTabs.APL, labelId: "DailyAnalysisAplApp", badge: 0 },
  ];

  return (
    <Container fluid className="daily-analysis">
      <Row className="da-header">
        <Col>
          <h4>
            <FormattedMessage id="DailyAnalysis" />
          </h4>
        </Col>
        <Col xs="auto">
          {/* Forward arrow is disabled on yesterday - there is never a report for today. */}
          <div className="da-datenav">
            <Button
              className="da-datenav__step"
              onClick={() => setDate(dateAddDays(date, -1))}
              title={intl.formatMessage({ id: "DailyAnalysisPreviousDay" })}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <span className="da-datenav__field">
              <FontAwesomeIcon icon={faCalendarDays} />
              <DatePicker
                selected={date}
                onChange={(value) => {
                  if (value) setDate(value);
                }}
                locale="sv"
                dateFormat="yyyy-MM-dd"
                maxDate={latest}
                className="da-datenav__input"
              />
            </span>
            <Button
              className="da-datenav__step"
              disabled={atLatest}
              onClick={() => setDate(dateAddDays(date, 1))}
              title={intl.formatMessage({ id: "DailyAnalysisNextDay" })}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </Col>
      </Row>

      <Nav tabs>
        {tabs.map((tab) => (
          <NavItem key={tab.id}>
            <NavLink
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <FormattedMessage id={tab.labelId} />
              {tab.badge ? (
                <span className="da-tab__count">{tab.badge}</span>
              ) : (
                ""
              )}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {isLoading ? (
        <div className="da-loading">
          <Spinner color="primary" />
        </div>
      ) : notFound ? (
        <Alert color="light" className="da-banner">
          <FormattedMessage
            id="DailyAnalysisNoAnalysis"
            values={{ date: dateStamp }}
          />
        </Alert>
      ) : isError ? (
        <Alert color="danger" className="da-banner">
          <FormattedMessage id="DailyAnalysisLoadFailed" />
        </Alert>
      ) : data ? (
        <div>
          {activeTab === AnalysisTabs.OVERVIEW ? (
            <DailyAnalysisOverview data={data} onSelectTab={setActiveTab} />
          ) : (
            ""
          )}
          {activeTab === AnalysisTabs.BACKEND ? (
            <DailyAnalysisBackend data={data} />
          ) : (
            ""
          )}
          {activeTab === AnalysisTabs.CLIENT ? (
            <DailyAnalysisClient data={data} />
          ) : (
            ""
          )}
          {activeTab === AnalysisTabs.SUPPORT ? (
            <DailyAnalysisSupport data={data} />
          ) : (
            ""
          )}
          {activeTab === AnalysisTabs.APL ? (
            <DailyAnalysisApl data={data} />
          ) : (
            ""
          )}
        </div>
      ) : (
        ""
      )}
    </Container>
  );
}

export default DailyAnalysis;
